import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  onSnapshot, 
  deleteDoc 
} from 'firebase/firestore';
import { 
  Calendar, 
  DollarSign, 
  Plus, 
  Trash2, 
  AlertCircle, 
  CheckCircle2, 
  Clock,
  TrendingUp,
  FileText,
  Edit2,
  X,
  Sparkles,
  ListTodo,
  Mail,
  Loader2,
  Copy,
  LogOut,
  User,
  ArrowLeft
} from 'lucide-react';

// --- Reusable UI Components (Moved from App.js) ---

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-200 ${className}`}>
    {children}
  </div>
);

const StatCard = ({ title, value, subValue, icon: Icon, colorClass }) => (
  <Card className="p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-slate-900">{value}</h3>
        {subValue && <p className="text-sm text-slate-400 mt-1">{subValue}</p>}
      </div>
      <div className={`p-3 rounded-lg ${colorClass}`}>
        <Icon size={24} />
      </div>
    </div>
  </Card>
);

const Badge = ({ children, type }) => {
  const styles = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    overdue: 'bg-rose-100 text-rose-700 border-rose-200',
    neutral: 'bg-slate-100 text-slate-700 border-slate-200'
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[type] || styles.neutral}`}>
      {children}
    </span>
  );
};

const Modal = ({ isOpen, onClose, title, children, maxWidth = "max-w-md" }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`bg-white rounded-xl shadow-xl ${maxWidth} w-full overflow-hidden animate-in fade-in zoom-in duration-200`}>
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// --- Gemini Helper (Passed as prop or imported if utility) ---
// For now, assuming it's passed or duplicated. Let's duplicate the helper or move it to a utility file later.
// To keep this self-contained, I'll accept a generateGeminiContent prop or callback.

const GeminiAssistantModal = ({ isOpen, onClose, milestone, onGenerate }) => {
  const [activeTab, setActiveTab] = useState('breakdown'); 
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  useEffect(() => {
    setResult('');
  }, [milestone, activeTab]);

  if (!milestone) return null;

  const handleGenerate = async () => {
    setLoading(true);
    let prompt = '';
    
    if (activeTab === 'breakdown') {
      prompt = `You are a project management expert. 
      Generate a concise, actionable checklist of 5 key sub-tasks required to achieve the milestone: "${milestone.name}".
      The milestone is due on ${milestone.date}. The budget is ${milestone.fixedCost}.
      Format as a simple bulleted list. Do not include introductory text.`;
    } else {
      prompt = `You are a professional project manager.
      Draft a brief, polite status update email to stakeholders regarding the milestone: "${milestone.name}".
      Status: ${milestone.status}. 
      Due Date: ${milestone.date}.
      Budget Status: Fixed cost is ${milestone.fixedCost}.
      Keep it under 100 words. Use placeholders like [Stakeholder Name] where appropriate.`;
    }

    const generatedText = await onGenerate(prompt);
    setResult(generatedText);
    setLoading(false);
  };

  const copyToClipboard = () => {
    if (!result) return;
    const textArea = document.createElement('textarea');
    textArea.value = result;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI Assistant" maxWidth="max-w-2xl">
      <div className="flex flex-col h-[500px]">
        <div className="bg-indigo-50 p-4 rounded-lg mb-4 border border-indigo-100">
          <div className="flex items-center gap-2 text-indigo-800 font-semibold mb-1">
            <Sparkles size={16} />
            Analyzing: {milestone.name}
          </div>
          <div className="text-indigo-600 text-sm">
            Due: {new Date(milestone.date).toLocaleDateString()} • Cost: ${milestone.fixedCost.toLocaleString()}
          </div>
        </div>

        <div className="flex gap-2 mb-4 border-b border-slate-100 pb-1">
          <button
            onClick={() => setActiveTab('breakdown')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
              activeTab === 'breakdown' 
                ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <ListTodo size={16} />
            Task Breakdown
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
              activeTab === 'email' 
                ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Mail size={16} />
            Draft Email
          </button>
        </div>

        <div className="flex-1 bg-slate-50 rounded-lg border border-slate-200 p-4 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 animate-pulse">
              <Loader2 size={32} className="animate-spin mb-2 text-blue-500" />
              <p>Generating with Gemini...</p>
            </div>
          ) : result ? (
            <div className="flex-1 flex flex-col overflow-hidden">
               <textarea 
                className="flex-1 w-full bg-transparent border-none resize-none focus:ring-0 text-slate-700 font-mono text-sm leading-relaxed p-0"
                value={result}
                readOnly
              />
              <div className="pt-3 flex justify-end">
                <button 
                  onClick={copyToClipboard}
                  className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
                >
                  <Copy size={14} />
                  Copy to Clipboard
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-center px-8">
              {activeTab === 'breakdown' ? (
                <>
                  <ListTodo size={48} className="mb-3 opacity-20" />
                  <p className="text-sm">Generate a smart checklist of sub-tasks required to complete this milestone on time.</p>
                </>
              ) : (
                <>
                  <Mail size={48} className="mb-3 opacity-20" />
                  <p className="text-sm">Draft a professional status update email for stakeholders based on current progress.</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium transition-all shadow-sm active:scale-95 ${
              activeTab === 'breakdown' 
                ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' 
                : 'bg-purple-600 hover:bg-purple-700 shadow-purple-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              'Thinking...'
            ) : (
              <>
                <Sparkles size={18} />
                {activeTab === 'breakdown' ? 'Generate Plan ✨' : 'Draft Email ✨'}
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default function ProjectDetails({ project: initialProject, user, db, appId, onBack, onLogout, generateContent }) {
  const [project, setProject] = useState(initialProject);
  const [milestones, setMilestones] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [isGeminiOpen, setIsGeminiOpen] = useState(false);
  const [selectedGeminiMilestone, setSelectedGeminiMilestone] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    date: '',
    fixedCost: '',
    expenses: '',
    isTBD: false
  });

  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({
    fees: project.budgetFees || 0,
    expenses: project.budgetExpenses || 0
  });

  useEffect(() => {
    if (!isEditingBudget) {
      setBudgetForm({
        fees: project.budgetFees || 0,
        expenses: project.budgetExpenses || 0
      });
    }
  }, [project, isEditingBudget]);

  // --- Project Data Listener ---
  useEffect(() => {
    if (!user || !initialProject) return;
    const projectRef = doc(db, 'artifacts', appId, 'projects', initialProject.id);
    const unsubProject = onSnapshot(projectRef, (doc) => {
        if (doc.exists()) {
            setProject({ id: doc.id, ...doc.data() });
        }
    });
    return () => unsubProject();
  }, [user, initialProject, db, appId]);

  const handleSaveBudget = async () => {
    if (!user || !project) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'projects', project.id), {
        budgetFees: Number(budgetForm.fees),
        budgetExpenses: Number(budgetForm.expenses)
      }, { merge: true });
      setIsEditingBudget(false);
    } catch (e) {
      console.error("Error saving budget:", e);
    }
  };

  // --- Data Loading ---
  useEffect(() => {
    if (!user || !project) return;
    
    const milestonesRef = collection(db, 'artifacts', appId, 'projects', project.id, 'milestones');
    const unsubMilestones = onSnapshot(milestonesRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      loaded.sort((a, b) => new Date(a.date) - new Date(b.date));
      setMilestones(loaded);
    }, (error) => {
      console.error("Error fetching milestones:", error);
    });

    return () => {
      unsubMilestones();
    };
  }, [user, project, db, appId]);

  // --- Calculations ---
  const stats = useMemo(() => {
    const totalFixed = milestones.reduce((sum, m) => sum + (Number(m.fixedCost) || 0), 0);
    const totalExpenses = milestones.reduce((sum, m) => sum + (Number(m.expenses) || 0), 0);
    const grandTotal = totalFixed + totalExpenses;
    const completedCount = milestones.filter(m => m.status === 'completed').length;
    const progress = milestones.length ? (completedCount / milestones.length) * 100 : 0;
    
    return { totalFixed, totalExpenses, grandTotal, progress };
  }, [milestones]);

  // --- Actions ---
  const handleSave = async () => {
    if (!user || !project) return;
    
    const newMilestone = {
      name: formData.name,
      date: formData.date,
      fixedCost: Number(formData.fixedCost),
      expenses: Number(formData.expenses),
      status: 'pending',
      isTBD: formData.isTBD,
      tbdText: formData.isTBD ? 'Date TBD' : null
    };

    try {
      const colRef = collection(db, 'artifacts', appId, 'projects', project.id, 'milestones');
      if (editingId) {
        await setDoc(doc(colRef, editingId), newMilestone, { merge: true });
      } else {
        const newDocRef = doc(colRef);
        await setDoc(newDocRef, { ...newMilestone, id: newDocRef.id });
      }
      closeModal();
    } catch (e) {
      console.error("Error saving:", e);
    }
  };

  const handleDelete = async (id) => {
    if (!user || !project) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'projects', project.id, 'milestones', id));
    } catch (e) {
      console.error("Error deleting:", e);
    }
  };

  const toggleStatus = async (milestone) => {
    if (!user || !project) return;
    const newStatus = milestone.status === 'completed' ? 'pending' : 'completed';
    await setDoc(doc(db, 'artifacts', appId, 'projects', project.id, 'milestones', milestone.id), {
      status: newStatus
    }, { merge: true });
  };

  // --- UI Helpers ---
  const openAddModal = () => {
    setEditingId(null);
    setFormData({ name: '', date: '', fixedCost: '', expenses: '', isTBD: false });
    setIsAddModalOpen(true);
  };

  const openEditModal = (m) => {
    setEditingId(m.id);
    setFormData({
      name: m.name,
      date: m.date,
      fixedCost: m.fixedCost,
      expenses: m.expenses,
      isTBD: m.isTBD || false
    });
    setIsAddModalOpen(true);
  };

  const openGeminiModal = (m) => {
    setSelectedGeminiMilestone(m);
    setIsGeminiOpen(true);
  };

  const closeModal = () => setIsAddModalOpen(false);

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  const formatDate = (dateStr, isTBD, tbdText) => {
    if (isTBD) return tbdText || 'TBD';
    if (!dateStr) return 'No Date';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        
        <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 rounded-full hover:bg-white hover:shadow-sm transition-all text-slate-500 hover:text-slate-800"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{project.name}</h1>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium border bg-slate-100 text-slate-600 border-slate-200">
                  {project.clientName}
                </span>
              </div>
              <p className="text-slate-500 mt-1">Manage milestones for this project.</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-3 items-center">
              <button 
                onClick={openAddModal}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-all shadow-sm hover:shadow active:scale-95"
              >
                <Plus size={18} />
                Add Milestone
              </button>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard 
            title="Total Fixed Fees" 
            value={formatCurrency(stats.totalFixed)} 
            subValue={`${milestones.length} Milestones`}
            icon={DollarSign} 
            colorClass="bg-blue-50 text-blue-600"
          />
          <StatCard 
            title="Incurred Expenses" 
            value={formatCurrency(stats.totalExpenses)} 
            subValue="Variable costs added"
            icon={TrendingUp} 
            colorClass="bg-amber-50 text-amber-600"
          />
          <div className="bg-slate-900 text-white rounded-xl shadow-sm p-6 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-2">
                <p className="text-sm font-medium text-slate-300">Budget Overview</p>
                <button 
                  onClick={() => isEditingBudget ? handleSaveBudget() : setIsEditingBudget(true)}
                  className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-emerald-400 transition-colors"
                >
                  {isEditingBudget ? <CheckCircle2 size={16} /> : <Edit2 size={16} />}
                </button>
              </div>

              {isEditingBudget ? (
                <div className="space-y-3 mt-2">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Fees Budget</label>
                    <input 
                      type="number"
                      value={budgetForm.fees}
                      onChange={(e) => setBudgetForm({...budgetForm, fees: e.target.value})}
                      className="w-full bg-black/20 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Expenses Budget</label>
                    <input 
                      type="number"
                      value={budgetForm.expenses}
                      onChange={(e) => setBudgetForm({...budgetForm, expenses: e.target.value})}
                      className="w-full bg-black/20 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 mt-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Fees</span>
                      <span className={stats.totalFixed > (project.budgetFees || 0) ? "text-rose-400" : "text-white"}>
                        {formatCurrency(stats.totalFixed)} <span className="text-slate-500">/ {formatCurrency(project.budgetFees || 0)}</span>
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full ${stats.totalFixed > (project.budgetFees || 0) ? "bg-rose-500" : "bg-blue-500"}`}
                        style={{ width: `${Math.min(((stats.totalFixed || 0) / (project.budgetFees || 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Expenses</span>
                      <span className={stats.totalExpenses > (project.budgetExpenses || 0) ? "text-rose-400" : "text-white"}>
                        {formatCurrency(stats.totalExpenses)} <span className="text-slate-500">/ {formatCurrency(project.budgetExpenses || 0)}</span>
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full ${stats.totalExpenses > (project.budgetExpenses || 0) ? "bg-rose-500" : "bg-amber-500"}`}
                        style={{ width: `${Math.min(((stats.totalExpenses || 0) / (project.budgetExpenses || 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/10 flex justify-between items-baseline">
                    <span className="text-xs font-medium text-slate-300 uppercase tracking-wider">Total</span>
                    <span className="text-xl font-bold">
                      {formatCurrency(stats.grandTotal)}
                      <span className="text-sm text-slate-500 font-normal ml-1">
                        / {formatCurrency((project.budgetFees || 0) + (project.budgetExpenses || 0))}
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <h2 className="font-semibold text-slate-900">Milestones</h2>
                <span className="text-sm text-slate-500">{Math.round(stats.progress)}% Complete</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-6 py-3 font-medium">Milestone</th>
                      <th className="px-6 py-3 font-medium">Date</th>
                      <th className="px-6 py-3 font-medium text-right">Fees + Exp.</th>
                      <th className="px-6 py-3 font-medium text-center">Status</th>
                      <th className="px-6 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {milestones.map((m) => (
                      <tr key={m.id} className="hover:bg-slate-50/50 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{m.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                             Fixed: {formatCurrency(m.fixedCost)} 
                             {m.expenses > 0 && <span className="text-amber-600 font-medium">+ {formatCurrency(m.expenses)} exp</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-slate-400" />
                            {formatDate(m.date, m.isTBD, m.tbdText)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-slate-900">
                          {formatCurrency((Number(m.fixedCost) || 0) + (Number(m.expenses) || 0))}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button 
                            onClick={() => toggleStatus(m)}
                            className={`transition-transform active:scale-95 ${m.status === 'completed' ? 'opacity-100' : 'opacity-50 hover:opacity-75'}`}
                          >
                            <Badge type={m.status === 'completed' ? 'completed' : 'pending'}>
                              {m.status === 'completed' ? 'Done' : 'Pending'}
                            </Badge>
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => openGeminiModal(m)} 
                              className="p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="Ask AI Assistant"
                            >
                              <Sparkles size={16} />
                            </button>
                            <button onClick={() => openEditModal(m)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(m.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {milestones.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                          No milestones yet. Add one to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Clock size={18} className="text-blue-500" />
                Timeline
              </h3>
              <div className="relative border-l-2 border-slate-100 ml-3 space-y-8 pb-2">
                {milestones.map((m, idx) => {
                   const isPast = new Date(m.date) < new Date() && !m.isTBD;
                   const isNext = !isPast && milestones.findIndex(x => new Date(x.date) > new Date()) === idx;
                   
                   return (
                    <div key={m.id} className="relative pl-8">
                      <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 transition-colors ${
                        m.status === 'completed' ? 'bg-emerald-500 border-emerald-500' : 
                        isPast ? 'bg-white border-rose-300' :
                        'bg-white border-slate-300'
                      }`}>
                        {m.status === 'completed' && <CheckCircle2 size={10} className="text-white absolute top-0.5 left-0.5" />}
                      </div>
                      
                      <div className={`${m.status === 'completed' ? 'opacity-50 grayscale' : ''} transition-all`}>
                        <span className={`text-xs font-semibold tracking-wider uppercase mb-1 block ${isPast && m.status !== 'completed' ? 'text-rose-500' : 'text-slate-400'}`}>
                          {formatDate(m.date, m.isTBD, m.tbdText)}
                        </span>
                        <p className="text-sm font-medium text-slate-900 leading-snug mb-1">{m.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{formatCurrency((Number(m.fixedCost) || 0) + (Number(m.expenses) || 0))}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>

      </div>

      <Modal
        isOpen={isAddModalOpen}
        onClose={closeModal}
        title={editingId ? 'Edit Milestone' : 'New Milestone'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input 
              type="text" 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="e.g. Clinic 1.5 Complete"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input 
                type="date" 
                value={formData.date}
                disabled={formData.isTBD}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={formData.isTBD}
                  onChange={(e) => setFormData({...formData, isTBD: e.target.checked})}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-600">Date is TBD</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fixed Fee ($)</label>
              <input 
                type="number" 
                value={formData.fixedCost}
                onChange={(e) => setFormData({...formData, fixedCost: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Incurred Exp. ($)</label>
              <input 
                type="number" 
                value={formData.expenses}
                onChange={(e) => setFormData({...formData, expenses: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button 
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm hover:shadow transition-all"
            >
              {editingId ? 'Save Changes' : 'Create Milestone'}
            </button>
          </div>
        </div>
      </Modal>

      <GeminiAssistantModal 
        isOpen={isGeminiOpen}
        onClose={() => setIsGeminiOpen(false)}
        milestone={selectedGeminiMilestone}
        onGenerate={generateContent}
      />

    </div>
  );
}
